import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import moment from "moment";
import { CategoryTag } from "../../shared/components/CategoryTag";
import { AdjustmentTag } from "../components/adjustment/AdjustmentTag";
import { GridButtons } from "../components/others/GridButtons";
import { DelayByShift } from "../models/DelayByShift";

type Props = {
	justifyData: (delay: DelayByShift) => void;
	splitData: (delay: DelayByShift) => void;
	adjustment: (delay: DelayByShift) => void;
	showContextMenu: Function;
};

export const useTableData = ({
	justifyData,
	splitData,
	adjustment,
	showContextMenu,
}: Props) => {
	const columnDefs: ColDef[] = [
		{
			field: "AssetName",
			headerName: "Asset",
			flex: 1,
			sortable: true,
		},
		{
			field: "Start",
			headerName: "Start",
			valueGetter: (params) =>
				dxtToLocalServerTime(params.data?.Start, "HH:mm:ss") || "",
			flex: 1,
			sortable: true,
		},
		{
			field: "End",
			headerName: "End",
			valueGetter: (params) =>
				dxtToLocalServerTime(params.data?.End, "HH:mm:ss") || "",
			flex: 1,
			sortable: true,
		},
		{
			field: "Duration",
			headerName: "Duration",
			valueFormatter: (params) =>
				moment.duration(params.value, "seconds").format("h[h] m[m] s[s]"),
			flex: 1,
			sortable: true,
		},
		{
			field: "DelayCategory",
			headerName: "Category",
			valueGetter: (params) => {
				const delayCategoryName = params.data?.DelayCategoryName;
				const delayCategoryColor = params.data?.DelayCategoryColor;
				return { name: delayCategoryName, color: delayCategoryColor };
			},
			cellRenderer: CategoryTag,
			flex: 1,
			sortable: true,
		},
		{
			field: "DelayCodePath",
			headerName: "Code",
			valueGetter: (params) => {
				const delayCodeName = params.data?.DelayCodeName;
				const delayCodePath = params.data?.DelayCodePath;
				const millAdjustmentId = params.data?.MillAdjustmentId;

				const isSystemDelayCode =
					delayCodeName === "Adjustment" ||
					delayCodeName === "Undeclared Mill" ||
					delayCodeName === "Undeclared Cold Shear";

				let displayPath = delayCodePath;
				const maxLength = 40;

				// If the path is longer than maxLength, intelligently truncate it
				if (delayCodePath && delayCodePath.length > maxLength) {
					let remainingPath = delayCodePath;

					// As long as the path is too long and has bars
					while (
						remainingPath.length > maxLength &&
						remainingPath.includes("/")
					) {
						// Delete the first segment of the path (up to the first bar)
						const firstSlashPos = remainingPath.indexOf("/");
						remainingPath = remainingPath.substring(firstSlashPos + 1);
					}

					// If we manage to reduce the path, add the prefix ".../"
					if (remainingPath !== delayCodePath) {
						displayPath = ".../" + remainingPath;
					}
				}

				return {
					name: isSystemDelayCode ? delayCodeName : displayPath,
					id: millAdjustmentId,
				};
			},
			cellRenderer: AdjustmentTag,
			flex: 3,
			cellStyle: {
				justifyContent: "flex-start",
			},
			wrapText: true,
			autoHeight: true,
		},
		{
			field: "Comments",
			headerName: "Comments",
			flex: 4,
			cellStyle: {
				justifyContent: "flex-start",
			},
			wrapText: true,
			autoHeight: true,
		},
		{
			field: "JustifiedByUser",
			headerName: "User",
			flex: 1,
			sortable: true,
		},
		{
			field: "JustifiedDatetime",
			headerName: "Justification Date",
			valueGetter: (params) =>
				dxtToLocalServerTime(params.data?.JustifiedDatetime, "MM-dd HH:mm"),
			flex: 2,
		},
		{
			field: "",
			headerName: "",
			cellRenderer: GridButtons,
			cellRendererParams: (params: ICellRendererParams) => ({
				data: params.data,
				justifyData: justifyData,
				splitData: splitData,
				adjustment: adjustment,
				showContextMenu: () =>
					showContextMenu(event, params.data, "delayManagerContext"),
			}),
			wrapText: false,
			wrapHeaderText: false,
			sortable: false,
			flex: 1,
			maxWidth: 180,
			minWidth: 180,
			filter: false,
		},
	];
	return { columnDefs };
};
